import React, { useState } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

const AnalysisCard = ({ data }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  // Destructure the data object
  const { images, prompt, output, id, created_at } = data;
  
  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);
  
  const nextImage = () => {
    setCurrentImageIndex((prevIndex) => 
      prevIndex === images.length - 1 ? 0 : prevIndex + 1
    );
  };
  
  const prevImage = () => {
    setCurrentImageIndex((prevIndex) => 
      prevIndex === 0 ? images.length - 1 : prevIndex - 1
    );
  };
  
  // Format date for display
  const formatDate = (isoDateTime) => {
    const date = new Date(isoDateTime);
    const options = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZone: 'America/Sao_Paulo' // Adjust timezone as needed
    };
    return date.toLocaleString('pt-BR', options);
  };
  
  const renderFormattedResponse = () => {

    // Convert markdown tables to HTML tables
    let formattedText = output;
    
    // Find markdown tables and convert them to HTML tables
    const tableRegex = /\|(.+)\|[\r\n]+\|([-:\s|]+)\|[\r\n]+((?:\|.+\|[\r\n]+)+)/g;
    formattedText = formattedText.replace(tableRegex, (match) => {
      // Split the table into rows
      const rows = match.split('\n').filter(row => row.trim() !== '');
      
      if (rows.length < 3) return match; // Not enough rows to be a proper table
      
      // Process the header row
      const headerRow = rows[0];
      const headerCells = headerRow.split('|').filter(cell => cell.trim() !== '');
      const headerHTML = `<tr>${headerCells.map(cell => `<th>${cell.trim()}</th>`).join('')}</tr>`;
      
      // Process the separator row (row 2) to determine alignment - skip for now
      
      // Process data rows
      const dataRowsHTML = rows.slice(2).map(row => {
        const cells = row.split('|').filter(cell => cell.trim() !== '');
        return `<tr>${cells.map(cell => `<td>${cell.trim()}</td>`).join('')}</tr>`;
      }).join('');
      
      return `<table class="markdown-table"><thead>${headerHTML}</thead><tbody>${dataRowsHTML}</tbody></table>`;
    });
    
    // Process other markdown elements
    formattedText = formattedText
      .replace(/#{1,6}\s+(.*)/g, '<h3>$1</h3>')
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      // Process non-table lists
      .replace(/^- (.*)/gm, '<li>$1</li>')
      .replace(/\n\n/g, '<br/>');
    
    // Wrap consecutive list items in ul tags
    let inList = false;
    formattedText = formattedText.split('\n').map(line => {
      if (line.trim().startsWith('<li>')) {
        if (!inList) {
          inList = true;
          return `<ul>${line}`;
        }
        return line;
      } else if (inList && line.trim() !== '') {
        inList = false;
        return `</ul><p>${line}</p>`;
      } else if (inList && line.trim() === '') {
        inList = false;
        return `</ul>`;
      } else if (line.trim() && !line.startsWith('<h3>') && !line.startsWith('<table')) {
        return `<p>${line}</p>`;
      }
      return line;
    }).join('');
    
    // Close any open lists at the end
    if (inList) {
      formattedText += '</ul>';
    }
    
    // Add CSS for tables
    const tableStyles = `
      <style>
        .markdown-table {
          border-collapse: collapse;
          width: 100%;
          margin: 16px 0;
        }
        .markdown-table th, .markdown-table td {
          border: 1px solid #ddd;
          padding: 8px;
          text-align: left;
        }
        .markdown-table th {
          background-color: #f2f2f2;
          font-weight: bold;
        }
        .markdown-table tr:nth-child(even) {
          background-color: #f9f9f9;
        }
      </style>
    `;
    
    return <div dangerouslySetInnerHTML={{ __html: tableStyles + formattedText }} />;
  };

  return (
    <div>
      {/* Card that opens the modal when clickedw-52 bg-white rounded-lg shadow-lg p-5 cursor-pointer */}
      <div 
        className="w-52 bg-white  overflow-hidden rounded-lg shadow-md cursor-pointer hover:shadow-lg transition-shadow duration-300"
        onClick={openModal}
      >
        {images && images.length > 0 && (
          <img 
            src={images[0]} 
            alt="Preview" 
            className="w-full h-48 object-cover"
          />
        )}
        <div className="p-4 bg-white">
          <h3 className="text-lg font-medium text-gray-900 truncate">Analise: {id}</h3>
          <p className="mt-1 text-sm text-gray-500">{formatDate(created_at)}</p>
        </div>
      </div>
      
      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-75">
          <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
            {/* Modal header */}
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-xl font-semibold text-gray-900">Image Details</h2>
              <button 
                onClick={closeModal}
                className="p-1 rounded-full hover:bg-gray-200 transition-colors"
              >
                <X size={24} />
              </button>
            </div>
            
            {/* Modal content */}
            <div className="flex flex-col md:flex-row overflow-y-auto p-4 gap-4">
              {/* Image gallery */}
              <div className="w-full md:w-1/2 relative">
                {images && images.length > 0 ? (
                  <div className="relative h-64 md:h-96">
                    <img 
                      src={images[currentImageIndex]} 
                      alt={`Image ${currentImageIndex + 1}`}
                      className="w-full h-full object-contain rounded"
                    />
                    
                    {/* Image navigation */}
                    {images.length > 1 && (
                      <>
                        <button 
                          onClick={(e) => { e.stopPropagation(); prevImage(); }}
                          className="absolute left-2 top-1/2 -translate-y-1/2 p-1 rounded-full bg-white/80 hover:bg-white shadow-md"
                        >
                          <ChevronLeft size={24} />
                        </button>
                        <button 
                          onClick={(e) => { e.stopPropagation(); nextImage(); }}
                          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full bg-white/80 hover:bg-white shadow-md"
                        >
                          <ChevronRight size={24} />
                        </button>
                        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-black/50 px-2 py-1 rounded text-white text-sm">
                          {currentImageIndex + 1} / {images.length}
                        </div>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="h-64 md:h-96 flex items-center justify-center bg-gray-100 rounded">
                    <p className="text-gray-500">No images available</p>
                  </div>
                )}
                
                {/* Thumbnails */}
                {images && images.length > 1 && (
                  <div className="flex mt-2 gap-2 overflow-x-auto">
                    {images.map((image, index) => (
                      <div 
                        key={index}
                        onClick={() => setCurrentImageIndex(index)}
                        className={`w-16 h-16 flex-shrink-0 cursor-pointer border-2 rounded ${currentImageIndex === index ? 'border-blue-500' : 'border-transparent'}`}
                      >
                        <img 
                          src={image} 
                          alt={`Thumbnail ${index + 1}`}
                          className="w-full h-full object-cover rounded"
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Details */}
              <div className="w-full md:w-1/2 flex flex-col">
                <div className="space-y-4">
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">ID</h3>
                    <p className="text-gray-900">{id}</p>
                  </div>
                  
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">Created At</h3>
                    <p className="text-gray-900">{formatDate(created_at)}</p>
                  </div>
                  
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">Prompt</h3>
                    <p className="text-gray-900 whitespace-pre-wrap">{prompt}</p>
                  </div>
                  
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">Output</h3>
                    <p className="text-gray-900 whitespace-pre-wrap">{renderFormattedResponse()}</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Modal footer */}
            <div className="p-4 border-t mt-auto">
              <button
                onClick={closeModal}
                className="w-full sm:w-auto px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded font-medium text-gray-800 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnalysisCard;